import { HttpClient } from '@angular/common/http';
import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { AuthenticationService, CoreService } from 'src/app/shared';

@Component({
  selector: 'exam-data-card',
  templateUrl: './exam-data-card.component.html',
  styleUrls: ['./exam-data-card.component.scss']
})
export class ExamDataCardComponent implements OnChanges, OnDestroy {
  @Input() candidateId: string;
  @Input() docType: string;
  @Input() title: string;

  imageSrc: string;
  downloadHref: string;
  downloadObjectUrl: string;
  downloadName: string;
  byteSize: number;
  unavailableMessage: string;
  imageLoadFailed: boolean;

  constructor(
    private coreService: CoreService,
    private auth: AuthenticationService,
    private http: HttpClient
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes.candidateId || changes.docType) && this.candidateId && this.docType) {
      this.loadDocument();
    }
  }

  ngOnDestroy(): void {
    this.revokeObjectUrls();
  }

  private loadDocument(): void {
    this.revokeObjectUrls();
    this.imageSrc = '';
    this.downloadHref = '';
    this.downloadObjectUrl = '';
    this.downloadName = this.getDownloadName();
    this.byteSize = 0;
    this.unavailableMessage = '';
    this.imageLoadFailed = false;

    const documentUrl = this.getDocumentUrl(this.candidateId, this.docType);
    if (!this.isFileDoc(this.docType)) {
      this.imageSrc = documentUrl;
      this.downloadHref = documentUrl;
      return;
    }

    this.http.get(documentUrl, { responseType: 'text' }).subscribe(res => {
      const rawData = res || '';
      if (this.isDocumentErrorResponse(rawData)) {
        this.unavailableMessage = 'Not available';
        return;
      }
      this.byteSize = this.getTextSize(rawData);
      if (rawData) {
        this.downloadObjectUrl = URL.createObjectURL(
          new Blob([rawData], { type: 'text/plain;charset=utf-8' })
        );
        this.downloadHref = this.downloadObjectUrl;
      } else {
        this.unavailableMessage = 'Not available';
      }
    }, () => {
      this.unavailableMessage = 'Not available';
    });
  }

  private getDocumentUrl(id: string, docType: string): string {
    let url = this.coreService.getRootPath() + `/documents/${id}/${docType}`;
    url += `?appname=${this.coreService.getAppName()}`;
    url += `&usertype=${this.coreService.getUserType()}`;
    url += `&token=${this.auth.getAuthToken()}`;
    return url;
  }

  private isFileDoc(docType: string): boolean {
    return !!docType && docType.endsWith('_iso');
  }

  private getDownloadName(): string {
    const extension = this.isFileDoc(this.docType) ? 'txt' : 'png';
    return `${this.candidateId}-${this.docType}.${extension}`;
  }

  private getTextSize(value: string): number {
    return new Blob([value || ''], { type: 'text/plain;charset=utf-8' }).size;
  }

  private isDocumentErrorResponse(value: string): boolean {
    if (!value) {
      return false;
    }

    try {
      const parsed = JSON.parse(value);
      return parsed && parsed.status === 'error';
    } catch {
      return false;
    }
  }

  onDownload(): void {
    if (!this.downloadHref) {
      return;
    }

    const link = document.createElement('a');
    link.href = this.downloadHref;
    link.download = this.downloadName;
    link.rel = 'noopener noreferrer';
    if (!this.downloadObjectUrl) {
      link.target = '_blank';
    }
    link.click();
  }

  onImageError(): void {
    this.imageLoadFailed = true;
    this.imageSrc = '';
    this.downloadHref = '';
    this.unavailableMessage = 'No image available';
  }

  private revokeObjectUrls(): void {
    if (this.downloadObjectUrl) {
      URL.revokeObjectURL(this.downloadObjectUrl);
      this.downloadObjectUrl = '';
    }
  }
}
